import { useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { JsonTreeView, tryParseJson } from '../parsed-feedback/JsonTreeView';
import './JsonViewer.css';

/**
 * Small icon-button that opens a fullscreen JSON viewer (Tree / Raw tabs +
 * copy). Mirrors ImageViewer's pattern — click to open, Escape or overlay
 * click to close. Hidden by default when the input is not parseable JSON.
 *
 * Usage:
 *   <JsonViewerButton text={rawResponse} title="Raw LLM Response" />
 */
export interface JsonViewerButtonProps {
    /** Raw text that may be JSON. Auto-detected via tryParseJson. */
    text?: string | null;
    /** Already-parsed value to render. Use this OR `text`. */
    value?: unknown;
    /** Modal title. */
    title?: string;
    /** When true and content isn't parseable JSON, the button is hidden.
     *  Default: true — keeps non-JSON sections visually quiet. */
    hideIfNotJson?: boolean;
    className?: string;
}

export function JsonViewerButton({
    text,
    value,
    title = 'JSON Viewer',
    hideIfNotJson = true,
    className,
}: JsonViewerButtonProps) {
    const [isOpen, setIsOpen] = useState(false);

    const resolved = useMemo(() => {
        if (value !== undefined) {
            return { parsed: value, isJson: true, rawText: safeStringify(value) };
        }
        if (text == null || text === '') return null;
        const tryResult = tryParseJson(text);
        if (tryResult.ok) {
            return { parsed: tryResult.value, isJson: true, rawText: text };
        }
        return { parsed: null, isJson: false, rawText: text };
    }, [text, value]);

    if (!resolved) return null;
    if (hideIfNotJson && !resolved.isJson) return null;

    return (
        <>
            <button
                type="button"
                className={`json-viewer-btn ${className || ''}`}
                onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
                title="Open in JSON viewer"
                aria-label="Open in JSON viewer"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H6a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2h2" />
                    <path d="M16 3h2a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-2" />
                </svg>
                <span className="json-viewer-btn-label">JSON</span>
            </button>
            {isOpen && (
                <JsonViewerModal
                    parsed={resolved.parsed}
                    rawText={resolved.rawText}
                    isJson={resolved.isJson}
                    title={title}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </>
    );
}

/**
 * Fullscreen viewer for a TEXT block — a prompt, a transcript, anything long
 * enough that reading it inside a scrolling <pre> is the bottleneck.
 *
 * Deliberately the same modal as {@link JsonViewerButton}: same overlay, same
 * Escape-to-close, same copy button, same monospaced body. A second modal that
 * behaved almost-but-not-quite the same would be worse than either.
 *
 * Usage:
 *   <TextViewerButton text={userPrompt} title="User Prompt Sent to LLM" />
 */
export interface TextViewerButtonProps {
    text?: string | null;
    /** Modal title. */
    title?: string;
    /** Hide the button below this length — a short block is already readable
     *  where it sits, and an expand affordance on it is just noise. */
    minLength?: number;
    className?: string;
}

export function TextViewerButton({
    text,
    title = 'Text',
    minLength = 400,
    className,
}: TextViewerButtonProps) {
    const [isOpen, setIsOpen] = useState(false);

    if (text == null || text.length < minLength) return null;

    return (
        <>
            <button
                type="button"
                className={`json-viewer-btn ${className || ''}`}
                onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
                title="Open in full screen"
                aria-label="Open in full screen"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h6v6" />
                    <path d="M9 21H3v-6" />
                    <path d="M21 3l-7 7" />
                    <path d="M3 21l7-7" />
                </svg>
                <span className="json-viewer-btn-label">FULL</span>
            </button>
            {isOpen && (
                <JsonViewerModal
                    parsed={null}
                    rawText={text}
                    isJson={false}
                    plainText
                    title={title}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </>
    );
}

function safeStringify(value: unknown): string {
    try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

interface JsonViewerModalProps {
    parsed: unknown;
    rawText: string;
    isJson: boolean;
    title: string;
    onClose: () => void;
    /** Content that is prose by nature (a prompt, a transcript). Suppresses the
     *  "not valid JSON" warning, which is only meaningful for something that
     *  was SUPPOSED to parse and didn't. */
    plainText?: boolean;
}

function JsonViewerModal({ parsed, rawText, isJson, title, onClose, plainText }: JsonViewerModalProps) {
    const [tab, setTab] = useState<'tree' | 'raw'>(isJson ? 'tree' : 'raw');
    const [copied, setCopied] = useState(false);

    const handleEscape = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleEscape);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = previousOverflow;
        };
    }, [handleEscape]);

    const prettyText = useMemo(
        () => (isJson ? safeStringify(parsed) : rawText),
        [isJson, parsed, rawText],
    );

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(prettyText);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard may be unavailable (insecure origin, perms) — silently no-op.
        }
    };

    return createPortal(
        <div
            className="jv-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="jv-panel" role="dialog" aria-label={title}>
                <div className="jv-toolbar">
                    <div className="jv-title">{title}</div>
                    {isJson && (
                        <div className="jv-tabs" role="tablist">
                            <button
                                role="tab"
                                aria-selected={tab === 'tree'}
                                className={`jv-tab ${tab === 'tree' ? 'active' : ''}`}
                                onClick={() => setTab('tree')}
                            >Tree</button>
                            <button
                                role="tab"
                                aria-selected={tab === 'raw'}
                                className={`jv-tab ${tab === 'raw' ? 'active' : ''}`}
                                onClick={() => setTab('raw')}
                            >Raw</button>
                        </div>
                    )}
                    <div className="jv-actions">
                        <button
                            className="jv-icon-btn"
                            onClick={handleCopy}
                            title={copied ? 'Copied!' : 'Copy to clipboard'}
                            aria-label="Copy to clipboard"
                        >
                            {copied ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                            )}
                        </button>
                        <button className="jv-icon-btn jv-close" onClick={onClose} title="Close" aria-label="Close">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="jv-body">
                    {!isJson && !plainText && (
                        <div className="jv-warning">
                            Content is not valid JSON — showing as raw text.
                        </div>
                    )}
                    {tab === 'tree' && isJson ? (
                        <JsonTreeView value={parsed} defaultOpen />
                    ) : (
                        <pre className="jv-raw">{prettyText}</pre>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
